import { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { FUNDING, type ReactPayPalScriptOptions } from '@paypal/react-paypal-js';
import { useProperty, useSiteContext } from '@eti/providers';
import { paymentMethodCodes } from '../../../constants/paymentMethods';
import { getMethod } from '../../../utils/paymentMethods';
import { initSession } from '../utils/session';
import type { TransformedPaymentMethod } from '../../../models';
import { getPayPalLocaleFromLocaleCode } from '../utils/locale';

const getPayPalClientId = (payPalSessionData: string | null): string | null | undefined => {
  if (!payPalSessionData) {
    return undefined;
  }

  try {
    return JSON.parse(window.atob(payPalSessionData))?.paypal?.clientId;
  } catch {
    return null;
  }
};

const usePayPalScriptOptions = (payPalSessionData: string | null): ReactPayPalScriptOptions => {
  const { siteCurrency, language, market } = useSiteContext();
  const { p } = useProperty();

  const payPalClientId = getPayPalClientId(payPalSessionData);

  const isSandboxEnabled = p('Payment.Method.PayPal.Sandbox.Enabled');

  return {
    ...(isSandboxEnabled ? { buyerCountry: market.code } : {}),
    clientId: payPalClientId ?? '',
    components: ['buttons', 'funding-eligibility', 'messages'],
    currency: siteCurrency.code,
    dataUserIdToken: payPalSessionData ?? '',
    enableFunding: FUNDING.PAYLATER,
    environment: isSandboxEnabled ? 'sandbox' : 'production',
    integrationDate: '2025-10-02',
    intent: 'authorize',
    locale: getPayPalLocaleFromLocaleCode(language.locale),
  };
};

export const usePayPal = (paymentMethods: TransformedPaymentMethod[]) => {
  const [payPalSessionData, setPayPalSessionData] = useState<string | null>(null);
  const payPalClientId = getPayPalClientId(payPalSessionData);
  const payPalScriptOptions = usePayPalScriptOptions(payPalSessionData);

  const hasPaypalPaymentMethod = Boolean(
    getMethod(paymentMethods, paymentMethodCodes.PAYPAL) ??
      getMethod(paymentMethods, paymentMethodCodes.PAYPAL_PAY_LATER),
  );

  useDeepCompareEffect(() => {
    const initPayPal = async () => {
      const sessionData = await initSession();
      setPayPalSessionData(sessionData.token);
    };
    if (hasPaypalPaymentMethod) {
      initPayPal();
    }
  }, [paymentMethods, hasPaypalPaymentMethod]);

  return {
    payPalSessionData,
    payPalClientId,
    hasPaypalPaymentMethod: hasPaypalPaymentMethod && payPalClientId !== null,
    payPalScriptOptions,
  };
};
